/**
 * Documents vault — global rollup grouped by property.
 *
 * Loan / personal documents reuse the canonical Profile Document Center so
 * this page stays in lock-step with `/loan/documents`. The vault then adds a
 * per-property accordion for contract-scoped documents (offer, closing, etc.).
 */

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Chip,
  CircularProgress,
  Container,
  Stack,
  Typography,
} from '@mui/material';

import { useGetContractDocumentsQuery } from '@/apis/extended/documentsApi';
import { LoanFlowPageFrame } from '@/components/Layout/Loan/LoanFlowStatusBar';
import { MortgageDocumentsWorkspace } from '@/components/Profile/MortgageDocumentsWorkspace';
import { useProperties } from '@/hooks/useProperties';

const StatusChip = ({ status }: { status: string }) => {
  const color =
    status === 'approved' || status === 'submitted'
      ? 'success'
      : status === 'pending_review'
      ? 'warning'
      : status === 'rejected'
      ? 'error'
      : 'default';
  return (
    <Chip
      label={status.replace(/_/g, ' ')}
      size="small"
      color={color}
      variant="outlined"
    />
  );
};

const PropertyDocumentsAccordion = ({
  propertyId,
  contractId,
  title,
}: {
  propertyId: string;
  contractId?: string;
  title: string;
}) => {
  const { data, isLoading } = useGetContractDocumentsQuery(contractId ?? '', {
    skip: !contractId,
  });
  const docs = data?.data ?? [];

  return (
    <Accordion key={propertyId}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ width: '100%', pr: 1 }}
        >
          <Typography sx={{ fontWeight: 700 }}>{title}</Typography>
          <Chip label={`${docs.length} docs`} size="small" />
        </Stack>
      </AccordionSummary>
      <AccordionDetails>
        {!contractId ? (
          <Typography variant="body2" color="text.secondary">
            Documents will appear here once you start an offer / refinance
            application.
          </Typography>
        ) : isLoading ? (
          <CircularProgress size={20} />
        ) : docs.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No documents uploaded yet.
          </Typography>
        ) : (
          <Stack gap={1}>
            {docs.map((d) => (
              <Box
                key={d._id}
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 2,
                  px: 2,
                  py: 1,
                }}
              >
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {d.title || d.type}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {d.category} · {new Date(d.uploadedAt).toLocaleDateString()}
                  </Typography>
                </Box>
                <StatusChip status={d.status} />
              </Box>
            ))}
          </Stack>
        )}
      </AccordionDetails>
    </Accordion>
  );
};

export const DocumentsVaultPage = () => {
  const { properties } = useProperties();

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
        Documents
      </Typography>

      {/* Mortgage / profile documents — same checklist as /loan/documents */}
      <Box sx={{ mb: 4 }}>
        <LoanFlowPageFrame>
          <MortgageDocumentsWorkspace variant="page" />
        </LoanFlowPageFrame>
      </Box>

      <Box sx={{ mt: 3 }}>
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
          Property documents
        </Typography>
        {properties.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            Add a property to begin tracking transaction documents here.
          </Typography>
        ) : (
          properties.map((p) => (
            <PropertyDocumentsAccordion
              key={p.id}
              propertyId={p.id}
              contractId={p.contractId}
              title={p.address || 'Property'}
            />
          ))
        )}
      </Box>
    </Container>
  );
};

export default DocumentsVaultPage;
